/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    getProp,
    CanBeSignal,
    PointLike,
    copyPointData,
    ContainerProps,
    $Container,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {Point} from 'pixi.js';

export type GameOrientationAlignConfiguration = {
    align?: PointLike;
    offset?: PointLike;
    scale?: PointLike;
    rotation?: number;
};

type GameOrientationAlignContainerProps = Omit<ContainerProps, 'position' | 'scale'> & {
    portrait: CanBeSignal<GameOrientationAlignConfiguration>;
    landscape: CanBeSignal<GameOrientationAlignConfiguration>;
};

/**
 * A component that helps to get a point on the screen,
 * in the game coordinate space.
 * It must contain one child container which will control.
 *
 * align [0,0] - will be the top left corner of the screen
 * align [1,1] - will be the bottom right corner of the screen
 * align [0.5, 0.5] - will be the center of the screen
 */

export const $GameOrientationAlignContainer: FC<GameOrientationAlignContainerProps> = (props) => {
    const tmpAlign = new Point();
    const tmpOffset = new Point();
    const tmpSale = new Point();

    const scale = Store.computed(() => {
        const isPortrait = slotStore.stage_safeZoneState_game.value!.isPortrait;
        const config = isPortrait ? getProp(props.portrait) : getProp(props.landscape);

        copyPointData(config.scale ?? 1, tmpSale);

        return new Point(tmpSale.x, tmpSale.y);
    });

    const position = Store.computed(() => {
        // the value of stage_safeZoneState_game updates on resize event
        const safeZone = slotStore.stage_safeZoneState_game.value!;
        const isPortrait = safeZone.isPortrait;
        const config = isPortrait ? getProp(props.portrait) : getProp(props.landscape);
        const {width, height} = safeZone.options.contentSize;

        copyPointData(config.align ?? 0, tmpAlign);

        const x = width * tmpAlign.x;
        const y = height * tmpAlign.y;

        return new Point(x, y);
    });

    const offset = Store.computed(() => {
        // the value of stage_safeZoneState_game updates on resize event
        const safeZone = slotStore.stage_safeZoneState_game.value!;
        const isPortrait = safeZone.isPortrait;
        const config = isPortrait ? getProp(props.portrait) : getProp(props.landscape);

        copyPointData(config.offset ?? 0, tmpOffset);

        return new Point(tmpOffset.x, tmpOffset.y);
    });

    const rotation = Store.computed(() => {
        const isPortrait = slotStore.stage_safeZoneState_game.value!.isPortrait;
        const config = isPortrait ? getProp(props.portrait) : getProp(props.landscape);

        return config.rotation ?? 0;
    });

    return <$Container
        name='GameOrientationAlignContainer'
        position={position}
        scale={scale}
    >
        <$Container {...props} position={offset} rotation={rotation}/>
    </$Container>;
};